// ─── Typologies clients CedoTrace ───────────────────────────────────────────
// Source unique de vérité pour les libellés et descriptions des types de
// tenant, alignée sur la note « Mise à jour CedoTrace 06/09/26 » (§ liste des
// clients potentiels). Utilisée par TenantSelector, SuperAdminConfig et la
// page d'inscription.
//
// Les 7 segments cibles de la note :
//   1. Particuliers (P2P)                       → particulier
//   2. Vendeurs sociaux (e-commerce informel)   → ecommerce
//   3. E-commerçants structurés (PME/marques)   → ecommerce_structure
//   4. Agences de transport interurbain          → transport_interurbain
//   5. Transitaires & cargos internationaux      → cargo_international / transit_douane
//   6. Transporteurs transfrontaliers            → inter_pays
//   7. Sociétés de livraison urbaine & coursiers → transport_local
//
// `multisite_distribution` n'est pas un segment commercial de la note mais
// reste une typologie interne (module Réseau/Hubs) — conservée en fin de liste.

export interface TenantTypeMeta {
  value: string;
  fr: string;
  en: string;
  descFr: string;
  descEn: string;
}

export const TENANT_TYPES: TenantTypeMeta[] = [
  {
    value: 'particulier',
    fr: 'Particulier (P2P)',
    en: 'Individual (P2P)',
    descFr: 'Envois occasionnels de personne à personne : documents, affaires personnelles, cadeaux.',
    descEn: 'Occasional person-to-person shipments: documents, personal belongings, gifts.',
  },
  {
    value: 'ecommerce',
    fr: 'Vendeur social (e-commerce informel)',
    en: 'Social seller (informal e-commerce)',
    descFr: 'Vente sur WhatsApp / Instagram / TikTok, gestion simple du paiement à la livraison (COD).',
    descEn: 'Selling on WhatsApp / Instagram / TikTok, simple cash-on-delivery (COD) handling.',
  },
  {
    value: 'ecommerce_structure',
    fr: 'E-commerçant structuré (PME / marque)',
    en: 'Structured e-merchant (SME / brand)',
    descFr: 'Boutique en ligne avec site web (Shopify, WooCommerce, API).',
    descEn: 'Online store with a website (Shopify, WooCommerce, API).',
  },
  {
    value: 'transport_interurbain',
    fr: 'Agence de transport interurbain (autocars)',
    en: 'Interurban transport agency (coaches)',
    descFr: 'Messagerie et transport de colis inter-villes (ex. General Express, Touristique).',
    descEn: 'Parcel messaging and intercity transport (e.g. General Express, Touristique).',
  },
  {
    value: 'cargo_international',
    fr: 'Transitaire / cargo international (air & mer)',
    en: 'Freight forwarder / international cargo (air & sea)',
    descFr: 'Groupage et conteneurs : Chine–Cameroun, Europe–Cameroun.',
    descEn: 'Consolidation and containers: China–Cameroon, Europe–Cameroon.',
  },
  {
    value: 'transit_douane',
    fr: 'Cabinet de transit et de douane',
    en: 'Customs & transit firm',
    descFr: 'Dédouanement, groupage international, gestion documentaire.',
    descEn: 'Customs clearance, international consolidation, document management.',
  },
  {
    value: 'inter_pays',
    fr: 'Transporteur transfrontalier',
    en: 'Cross-border carrier',
    descFr: 'Transport routier sous-régional : Cameroun ↔ Tchad, Gabon, Guinée Éq.',
    descEn: 'Sub-regional road transport: Cameroon ↔ Chad, Gabon, Eq. Guinea.',
  },
  {
    value: 'transport_local',
    fr: 'Société de livraison urbaine & coursiers',
    en: 'Urban delivery company & couriers',
    descFr: 'Flottes de motos / vans pour le dernier kilomètre intra-ville.',
    descEn: 'Motorbike / van fleets for last-mile intra-city delivery.',
  },
  {
    value: 'multisite_distribution',
    fr: 'Entreprise multisite / distribution',
    en: 'Multi-site / distribution company',
    descFr: "Réseau de hubs et d'agences, distribution multi-sites.",
    descEn: 'Network of hubs and agencies, multi-site distribution.',
  },
];

/** Libellé localisé d'un type de tenant (repli sur la valeur brute si inconnue). */
export function tenantTypeLabel(value: string | null | undefined, lang: 'fr' | 'en' = 'fr'): string {
  const meta = TENANT_TYPES.find((t) => t.value === value);
  return meta ? meta[lang] : value || '—';
}
